"use client";

import { PlusCircle, X } from "lucide-react";
import { useRef } from "react";

type FileInputProps = {
  label: string;
  accept?: string;
  multiple?: boolean;
  error?: string;
  previewFiles?: (File | string)[];
  onChange: (files: (File | string)[] | null) => void;
  // Controle opcional para marcar remoção de arquivo existente (ex: banner/logo já salvo)
  originalFileId?: number;
  markedForRemoval?: boolean;
  onMarkRemove?: (fileId: number) => void;
  removeButtonLabel?: string; // Texto do botão de remoção externo
};

const FileInput = ({
  label,
  accept,
  multiple,
  error,
  previewFiles = [],
  onChange,
  originalFileId,
  markedForRemoval,
  onMarkRemove,
  removeButtonLabel,
}: FileInputProps) => {
  const replaceInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const totalSlots = multiple ? 3 : 1;
  const previewList = [...previewFiles]; // mantém a ordem
  const filledSlots = previewList.length;
  const emptySlots = Math.max(totalSlots - filledSlots, 0);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    replaceIndex?: number,
  ) => {
    const incomingFiles = Array.from(e.target.files ?? []);

    if (replaceIndex !== undefined && incomingFiles.length) {
      const updated = [...previewList];
      updated[replaceIndex] = incomingFiles[0];
      onChange(updated);
    } else if (multiple) {
      const allowedCount = totalSlots - previewFiles.length;
      const newFiles = incomingFiles.slice(0, allowedCount);
      const updated = [...previewFiles, ...newFiles].slice(0, totalSlots);
      onChange(updated.length > 0 ? updated : null);
    } else {
      onChange(incomingFiles.length ? [incomingFiles[0]] : null);
    }
  };

  const handleRemove = (index: number) => {
    // Se for um arquivo existente (string) e temos o ID original, dispara marcação
    const file = previewList[index];
    if (originalFileId && typeof file === "string" && onMarkRemove) {
      onMarkRemove(originalFileId);
    }
    const updated = previewList.filter((_, i) => i !== index);
    onChange(updated.length > 0 ? updated : null);
  };

  return (
    <div className="mb-4">
      <div className="flex flex-wrap gap-10 justify-center md:justify-start">
        {previewList.map((file, i) => (
          <div
            key={i}
            className={`relative group rounded-lg overflow-hidden shadow-md ${markedForRemoval ? "opacity-40 ring-2 ring-red-500" : ""}`}
          >
            <img
              src={typeof file === "string" ? file : URL.createObjectURL(file)}
              alt={`preview-${i}`}
              className="w-60 h-52 object-contain rounded-lg cursor-pointer"
              onClick={() => replaceInputRefs.current[i]?.click()}
            />
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-100 transition"
              aria-label="Remover arquivo"
            >
              <X className="w-4 h-4 text-red-500" />
            </button>
            {originalFileId && onMarkRemove && (
              <button
                type="button"
                onClick={() => onMarkRemove(originalFileId)}
                className={`absolute bottom-1 left-1 px-2 py-1 text-[10px] rounded bg-white/80 backdrop-blur border ${markedForRemoval ? "border-red-600 text-red-600" : "border-gray-300 text-gray-600 hover:bg-red-600 hover:text-white hover:border-red-600"} transition`}
              >
                {markedForRemoval
                  ? removeButtonLabel
                    ? removeButtonLabel
                    : "Desfazer"
                  : removeButtonLabel
                    ? removeButtonLabel
                    : "Remover"}
              </button>
            )}
            <input
              ref={(el) => {
                replaceInputRefs.current[i] = el;
              }}
              type="file"
              accept={accept}
              onChange={(e) => handleFileChange(e, i)}
              className="hidden"
            />
          </div>
        ))}

        {[...Array(emptySlots)].map((_, idx) => (
          <label
            key={`empty-${idx}`}
            className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg w-60 h-52 cursor-pointer hover:border-[var(--primarySystem)] transition"
          >
            <PlusCircle className="w-8 h-8 text-gray-400" />
            <input
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={(e) => handleFileChange(e)}
              className="hidden"
            />
          </label>
        ))}
      </div>

      <label className="block text-xs font-medium text-gray-500 mt-2">
        {label}
        {originalFileId && onMarkRemove && (
          <span className="ml-2 text-[10px] text-gray-400">
            {markedForRemoval ? " (marcado para remoção)" : ""}
          </span>
        )}
      </label>

      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
    </div>
  );
};

export default FileInput;
